import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../Layout/DashboardLayout";
import DashboardTopbar from "../Layout/DashboardTopbar";
import useTokenBalance from "../hooks/useTokenBalance";
import { useMyApiContext } from "../Context/MyApiContext";
import { useApiGenerationContext } from "../Context/ApiGenerationContext";
import { useAuthContext } from "../Context/AuthContext";
import MyApiFilters from "./MyApis/MyApiFilters";
import MyApiCard from "./MyApis/MyApiCard";
import MyApiAddCard from "./MyApis/MyApiAddCard";
import { getPersistedMyApisUiState } from "../utils/myApisPersistence";
import "./MyApis/my-apis.css";

const MyApis = () => {
  const persistedUi = getPersistedMyApisUiState();
  const { fetchList, destroy, list, deleting, fetching, filters, setFilters, clearFilters } =
    useMyApiContext();
  const { refreshSession, user } = useAuthContext();
  const {
    setCurrentModel,
    setSelectedFields,
    setRequiredFields,
    setFieldType,
    setSelectedMethod,
    setSelectedRecord,
    setSelectedConfig,
    prevModel,
    setModelRecords
  } = useApiGenerationContext();
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { tokenBalance } = useTokenBalance();
  const [refreshing, setRefreshing] = useState(false);

  const updateFilterValue = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const mappedList = useMemo(() => {
    return list.map(({ fileContent, filePath, size }) => {
      try {
        return { ...JSON.parse(fileContent), filePath, size };
      } catch (e) {
        return {};
      }
    });
  }, [list]);

  const configOptions = useMemo(() => {
    return mappedList
      .map(({ config }) => config)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((e) => [e, e]);
  }, [mappedList]);

  const modelOptions = useMemo(() => {
    return mappedList
      .map(({ model }) => model)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((e) => [e, e]);
  }, [mappedList]);

  const actionOptions = useMemo(() => {
    return mappedList
      .map(({ action }) => action)
      .filter((v, i, a) => a.indexOf(v) === i)
      .map((e) => [e, e]);
  }, [mappedList]);

  const filteredList = useMemo(() => {
    return mappedList.filter(({ filename: name, config, model, action }) => {
      return (
        new RegExp(filters.search, "gi").test(name) &&
        (!filters.config || config === filters.config) &&
        (!filters.model || model === filters.model) &&
        (!filters.action || action === filters.action)
      );
    });
  }, [mappedList, filters]);

  const uniqueConfigCount = useMemo(() => {
    return new Set(mappedList.map(({ config }) => config).filter(Boolean)).size;
  }, [mappedList]);

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;

    const execute = async () => {
      const hasCache = persistedUi.hasCache;
      if (hasCache) {
        setRefreshing(true);
      }

      try {
        await fetchList({ background: hasCache });
      } catch (e) {
        try {
          await refreshSession();
          await fetchList({ background: hasCache });
        } catch (retryError) {
          console.log("fetching list failed", retryError);
        }
      } finally {
        if (!cancelled) {
          setRefreshing(false);
        }
      }
    };

    execute();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleDelete = async (api) => {
    if (confirm(t("Are you sure to delete?"))) {
      await destroy(api.config, api.filePath);
      await fetchList();
    }
  };

  const onView = (api) => {
    const inputFields = [...(api.inputFields || [])];
    if (inputFields.indexOf("display_name") < 0) {
      inputFields.push("display_name");
    }
    setSelectedConfig(api.config);
    setCurrentModel(api.model);
    setSelectedMethod(api.action);
    setSelectedRecord(api.record);
    setSelectedFields(inputFields);
    setRequiredFields(api.required);
    setFieldType(api.properties);
    navigate("/api-view");
  };

  const onEdit = (api) => {
    setSelectedConfig(api.config);
    setCurrentModel(api.model);
    setSelectedMethod(api.action);
    setSelectedRecord(api.record);
    setSelectedFields(api.fields);
    setRequiredFields(api.fields);
    setFieldType(api.fields);
    if (prevModel !== api.model) {
      setModelRecords([]);
    }
    navigate("/api-generator");
  };

  const goToGenerator = () => navigate("/api-generator");

  return (
    <DashboardLayout>
      <div className="dashboard-page my-apis-page">
        <DashboardTopbar title={t("My APIs")} tokenBalance={tokenBalance}>
          <Link to="/api-generator" className="btn btn-primary btn-sm">
            {t("+ New API")}
          </Link>
        </DashboardTopbar>

        <div className="page-content">
          <div className="page-header">
            <h1>{t("My APIs")}</h1>
            <p>
              {t("{{count}} endpoints configured across {{configs}} Odoo connections", {
                count: mappedList.length,
                configs: uniqueConfigCount
              })}
            </p>
            {refreshing ? (
              <p className="dashboard-loading" style={{ marginTop: 8 }}>
                {t("Fetching...")}
              </p>
            ) : null}
          </div>

          <MyApiFilters
            configOptions={configOptions}
            modelOptions={modelOptions}
            actionOptions={actionOptions}
            filters={filters}
            onFilterChange={updateFilterValue}
            onClear={clearFilters}
          />

          {fetching ? (
            <div className="dashboard-loading">{t("Fetching...")}</div>
          ) : (
            <>
              {list.length === 0 && (
                <div className="dashboard-empty">{t("There is no saved APIs here...")}</div>
              )}
              {list.length > 0 && filteredList.length === 0 && (
                <div className="dashboard-empty">
                  {t("The API you're looking for couldn't be found!")}
                </div>
              )}
              {(list.length === 0 || filteredList.length > 0) && (
                <div className="grid-auto">
                  {filteredList.map((api, i) => (
                    <MyApiCard
                      key={`${api.filePath || api.filename}-${i}`}
                      api={api}
                      deleting={deleting}
                      onView={onView}
                      onEdit={onEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                  <MyApiAddCard onClick={goToGenerator} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MyApis;
